import { useState, useEffect, useCallback, useRef } from "react";
import type { Product } from "@/components/DesignResults";
import { supabase } from "@/integrations/supabase/client";
import {
  track,
  incrementPersonProperty,
  productItemId,
  parsePriceNumber,
} from "@/lib/posthog";
import { trackBrevo, touchActivity } from "@/lib/brevo";

const STORAGE_KEY = "saved-furniture-items";

function readLocalItems(): Product[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeLocalItems(items: Product[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota / private mode — ignore */
  }
}

function sameProduct(a: Product, b: Product) {
  return a.title === b.title && a.source === b.source;
}

type SavedRow = {
  product_title: string;
  product_source: string;
  product_url: string | null;
  product_image_url: string | null;
  product_price: string | null;
  design_id: string | null;
};

function rowToProduct(row: SavedRow): Product {
  return {
    title: row.product_title,
    source: row.product_source,
    price: row.product_price ?? "",
    link: row.product_url ?? "",
    thumbnail: row.product_image_url ?? "",
    designId: row.design_id ?? null,
  } as Product;
}

export type SaveContext = {
  designId?: string | null;
  source?: "card" | "view_prompt" | "coach_mark" | "saved_items_sheet" | string;
};

export function useSavedItems() {
  const [savedItems, setSavedItems] = useState<Product[]>(readLocalItems);
  const [userId, setUserId] = useState<string | null>(null);
  const migratedRef = useRef(false);

  // Track auth state
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setUserId(data.session?.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
      if (!session?.user) migratedRef.current = false;
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // When signed in: load from DB and migrate any localStorage entries once.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    (async () => {
      // One-shot migration of any anonymous saves
      if (!migratedRef.current) {
        migratedRef.current = true;
        const local = readLocalItems();
        if (local.length > 0) {
          const rows = local.map((p) => ({
            user_id: userId,
            product_title: p.title,
            product_source: p.source,
            product_url: p.link ?? null,
            product_image_url: p.thumbnail ?? null,
            product_price: p.price ?? null,
            design_id: p.designId ?? null,
          }));
          await supabase.from("saved_items").upsert(rows, {
            onConflict: "user_id,product_title,product_source",
            ignoreDuplicates: true,
          });
          writeLocalItems([]);
        }
      }

      const { data, error } = await supabase
        .from("saved_items")
        .select("product_title,product_source,product_url,product_image_url,product_price,design_id")
        .order("created_at", { ascending: false });
      if (cancelled || error || !data) return;
      setSavedItems(data.map((r) => rowToProduct(r as SavedRow)));
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Persist to localStorage only while signed out (DB is source of truth when signed in)
  useEffect(() => {
    if (userId) return;
    writeLocalItems(savedItems);
  }, [savedItems, userId]);

  const addItem = useCallback(
    (product: Product, context?: SaveContext) => {
      setSavedItems((prev) => {
        if (prev.some((p) => sameProduct(p, product))) return prev;
        const withDesign: Product = { ...product, designId: context?.designId ?? product.designId ?? null };
        const next = [withDesign, ...prev];
        track("furniture_item_saved", {
          item_id: productItemId(product),
          item_price: parsePriceNumber(product.price),
          item_brand: product.source,
          design_id: context?.designId ?? undefined,
          save_source: context?.source ?? "card",
          saved_total_after: next.length,
        });
        void trackBrevo("furniture_save", {
          item_id: productItemId(product),
          item_brand: product.source,
          design_id: context?.designId ?? undefined,
        });
        touchActivity();
        incrementPersonProperty("total_saves", 1);
        return next;
      });
      if (userId) {
        supabase
          .from("saved_items")
          .insert({
            user_id: userId,
            product_title: product.title,
            product_source: product.source,
            product_url: product.link ?? null,
            product_image_url: product.thumbnail ?? null,
            product_price: product.price ?? null,
            design_id: context?.designId ?? null,
          })
          .then(({ error }) => {
            if (error && error.code !== "23505") {
              console.error("[saved_items] insert failed", error);
            }
          });
      }
    },
    [userId]
  );

  const removeItem = useCallback(
    (product: Product, context?: SaveContext) => {
      setSavedItems((prev) => {
        const next = prev.filter((p) => !sameProduct(p, product));
        track("furniture_item_removed", {
          item_id: productItemId(product),
          item_brand: product.source,
          design_id: context?.designId ?? undefined,
          saved_total_after: next.length,
        });
        return next;
      });
      if (userId) {
        supabase
          .from("saved_items")
          .delete()
          .eq("user_id", userId)
          .eq("product_title", product.title)
          .eq("product_source", product.source)
          .then(({ error }) => {
            if (error) console.error("[saved_items] delete failed", error);
          });
      }
    },
    [userId]
  );

  const isItemSaved = useCallback(
    (product: Product) => savedItems.some((p) => sameProduct(p, product)),
    [savedItems]
  );

  const clearAll = useCallback(() => {
    setSavedItems((prev) => {
      track("furniture_items_cleared", { count: prev.length });
      return [];
    });
    if (userId) {
      supabase
        .from("saved_items")
        .delete()
        .eq("user_id", userId)
        .then(({ error }) => {
          if (error) console.error("[saved_items] clearAll failed", error);
        });
    }
  }, [userId]);

  return { savedItems, addItem, removeItem, isItemSaved, clearAll };
}
